import React, { useState, useEffect } from 'react';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, passwordTooShort } from '../lib/passwordRule';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Save, User, Lock, Briefcase, Gift, CalendarDays, Building2,
  Phone, MapPin, FileText, AlertCircle, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { profileApi, dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import TwoFactorSetup from '../components/TwoFactorSetup';
import CalendarFeedCard from '../components/CalendarFeedCard';
import CalendarConnectionsCard from '../components/CalendarConnectionsCard';
import NotificationChannelCard from '../components/NotificationChannelCard';
import { format } from 'date-fns';
import { roleLabel, roleBadgeClass } from '../lib/roleBadges';
import WhereSignedIn from '../components/security/WhereSignedIn';

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser, replaceToken } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeSection, setActiveSection] = useState('profile'); // 'profile' or 'mydetails'
  
  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    birthday: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Only fetch approvals if user is a manager or admin
      const requests = [
        profileApi.get(),
        profileApi.getAllowances(),
      ];
      
      if (isManager) {
        requests.push(dashboardApi.getPendingApprovals());
      }
      
      const results = await Promise.all(requests);
      
      setProfile(results[0].data);
      setAllowances(results[1].data.allowances || []);
      if (isManager && results[2]) {
        setPendingCount(results[2].data.length);
      }
      
      setFormData({
        first_name: results[0].data.first_name || '',
        last_name: results[0].data.last_name || '',
        job_title: results[0].data.job_title || '',
        birthday: results[0].data.birthday || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await profileApi.update(formData);
      toast.success('Profile updated successfully');
      fetchData();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    const tooShort = passwordTooShort(passwordData.new_password);
    if (tooShort) {
      toast.error(tooShort);
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await profileApi.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      // Every other device is signed out; this one carries on with a fresh session (S1, 1A).
      if (response.data?.access_token) replaceToken(response.data.access_token);
      toast.success('Password changed. Any other devices you were signed in on have been signed out.');
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="font-heading text-2xl font-bold">My Profile</h1>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="px-6 flex gap-1">
            <button
              onClick={() => setActiveSection('profile')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeSection === 'profile'
                  ? 'bg-card text-foreground border-t border-l border-r border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 inline mr-2" />
              Profile & Settings
            </button>
            <button
              onClick={() => setActiveSection('mydetails')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeSection === 'mydetails'
                  ? 'bg-card text-foreground border-t border-l border-r border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="my-details-tab"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              My Details
            </button>
          </div>
        </header>

        <div className="p-6 max-w-3xl space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeSection === 'mydetails' ? (
            /* My Details Section - Read Only */
            <>
              <div className="card-hive p-4 bg-blue-500/10 border-blue-500/30">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-500" />
                  <p className="text-sm text-blue-100">
                    This is information held on your HR record. Contact your administrator if anything needs updating.
                  </p>
                </div>
              </div>
              
              {/* Employment Details */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Employment Details</h3>
                    <p className="text-sm text-muted-foreground">Your work information</p>
                  </div>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Employee ID</label>
                    <p className="text-foreground">{profile?.employee_id || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Payroll ID</label>
                    <p className="text-foreground">{profile?.payroll_id || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Job Title</label>
                    <p className="text-foreground">{profile?.job_title || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Department</label>
                    <p className="text-foreground">{profile?.department_name || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Start Date</label>
                    <p className="text-foreground">
                      {profile?.start_date ? format(new Date(profile.start_date), 'd MMMM yyyy') : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Notice Period</label>
                    <p className="text-foreground">
                      {profile?.notice_period_weeks ? `${profile.notice_period_weeks} weeks` : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Hours Per Week</label>
                    <p className="text-foreground">{profile?.hours_per_week || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Annual Allowance</label>
                    <p className="text-foreground">
                      {profile?.annual_allowance ? `${profile.annual_allowance} days` : '—'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Personal Details */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Personal Details</h3>
                    <p className="text-sm text-muted-foreground">Your personal information</p>
                  </div>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Full Name</label>
                    <p className="text-foreground">{profile?.first_name} {profile?.last_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
                    <p className="text-foreground">{profile?.email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Date of Birth</label>
                    <p className="text-foreground">
                      {profile?.date_of_birth ? format(new Date(profile.date_of_birth), 'd MMMM yyyy') : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Mobile Phone</label>
                    <p className="text-foreground">{profile?.mobile_phone || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Home Phone</label>
                    <p className="text-foreground">{profile?.home_phone || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Work Phone</label>
                    <p className="text-foreground">{profile?.work_phone || '—'}</p>
                  </div>
                </div>
              </div>
              
              {/* Address */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Address</h3>
                    <p className="text-sm text-muted-foreground">Your home address on file</p>
                  </div>
                </div>
                
                <div className="space-y-1">
                  {profile?.address_line_1 && <p className="text-foreground">{profile.address_line_1}</p>}
                  {profile?.address_line_2 && <p className="text-foreground">{profile.address_line_2}</p>}
                  {profile?.address_line_3 && <p className="text-foreground">{profile.address_line_3}</p>}
                  {profile?.town && <p className="text-foreground">{profile.town}</p>}
                  {profile?.county && <p className="text-foreground">{profile.county}</p>}
                  {profile?.postcode && <p className="text-foreground">{profile.postcode}</p>}
                  {profile?.country && <p className="text-foreground">{profile.country}</p>}
                  {!profile?.address_line_1 && !profile?.town && !profile?.postcode && (
                    <p className="text-muted-foreground italic">No address on file</p>
                  )}
                </div>
              </div>
              
              {/* Emergency Contacts */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Emergency Contacts</h3>
                    <p className="text-sm text-muted-foreground">Who to contact in an emergency</p>
                  </div>
                </div>
                
                {profile?.emergency_contacts && profile.emergency_contacts.length > 0 ? (
                  <div className="space-y-4">
                    {profile.emergency_contacts.map((contact, idx) => (
                      <div key={idx} className="p-4 bg-secondary/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{contact.name}</p>
                          {contact.relation && (
                            <span className="text-xs bg-secondary px-2 py-0.5 rounded">{contact.relation}</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {contact.phone && <p>Phone: {contact.phone}</p>}
                          {contact.mobile && <p>Mobile: {contact.mobile}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No emergency contacts on file</p>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Profile Header */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-2xl">
                    {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-xl">
                      {profile?.first_name} {profile?.last_name}
                    </h2>
                    <p className="text-muted-foreground">{profile?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {profile?.role && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadgeClass(profile.role)}`}>
                          {roleLabel(profile.role)}
                        </span>
                      )}
                      {profile?.department_name && (
                        <span className="text-xs text-muted-foreground">
                          • {profile.department_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Leave Allowances */}
              {allowances.length > 0 && (
                <div className="card-hive p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold">Leave Balance</h3>
                      <p className="text-sm text-muted-foreground">Your allowance for this year</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {allowances.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: a.leave_type_color }}
                          />
                          <span className="font-medium">{a.leave_type}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            Used: <span className="text-foreground">{a.used}</span>
                          </span>
                          {a.pending > 0 && (
                            <span className="text-yellow-500">
                              Pending: {a.pending}
                            </span>
                          )}
                          <span className="font-bold text-primary">
                            {a.remaining} remaining
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Edit Profile Form */}
              <form onSubmit={handleSubmit}>
                <div className="card-hive p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold">Personal Information</h3>
                      <p className="text-sm text-muted-foreground">Update your personal details</p>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium mb-2">First Name</label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        data-testid="first-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Last Name</label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        data-testid="last-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Job Title</label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={formData.job_title}
                          onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                          placeholder="e.g. Software Developer"
                          className="pl-10"
                          data-testid="job-title-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Birthday</label>
                      <div className="relative">
                        <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="date"
                          value={formData.birthday}
                          onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                          className="pl-10"
                          data-testid="birthday-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-border">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {profile?.start_date && (
                        <span>Started: {format(new Date(profile.start_date), 'd MMM yyyy')}</span>
                      )}
                      <span>Joined: {format(new Date(profile?.created_at), 'd MMM yyyy')}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-6">
                    <Button 
                      type="submit" 
                      className="btn-primary min-w-32"
                      disabled={saving}
                      data-testid="save-profile-btn"
                    >
                      {saving ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>

              {/* Change Password */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Security</h3>
                    <p className="text-sm text-muted-foreground">Change your password</p>
                  </div>
                </div>
                
                {!showPasswordForm ? (
                  <Button 
                    variant="outline"
                    onClick={() => setShowPasswordForm(true)}
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Current Password</label>
                      <Input
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        data-testid="current-password-input"
                      />
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium mb-2">New Password</label>
                        <Input
                          type="password"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          minLength={MIN_PASSWORD_LENGTH}
                          placeholder={PASSWORD_HINT}
                          data-testid="new-password-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Confirm Password</label>
                        <Input
                          type="password"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        className="bg-red-600 hover:bg-red-700"
                        disabled={changingPassword}
                        data-testid="save-password-btn"
                      >
                        {changingPassword ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* Where you're signed in, and your recent sign-ins (DESIGN-security.md, idea 8). */}
              <div className="card-hive p-6">
                <WhereSignedIn />
              </div>

              {/*
                Two-factor authentication. Its own component (components/TwoFactorSetup.jsx)
                rather than another 250 lines in here — this file is already 614.
              */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Two-factor authentication</h3>
                    <p className="text-sm text-muted-foreground">
                      An extra step when you sign in
                    </p>
                  </div>
                </div>
                <TwoFactorSetup />
              </div>

              {/*
                Where leave notifications go — decision A of DESIGN-teams.md.

                ⚠️ **BRINGS ITS OWN `card-hive` WRAPPER, for the same reason as the
                connections card below**: what it renders changes depending on whether the
                person has added the HolidayHive app in Teams, so the frame has to belong
                to the component that knows.

                ⚠️ ABOVE the calendar cards deliberately — this one governs messages the
                person gets whether or not they ever touch a calendar, so it is the more
                consequential setting of the three.
              */}
              <NotificationChannelCard />

              {/*
                Microsoft calendar connection.

                ⚠️ ABOVE the calendar feed card, deliberately. It is the half that reaches
                Outlook users whose Microsoft 365 tenant refuses internet calendar
                subscriptions — which is Rob's own account, and which the feed card cannot
                help with at all. Timetastic order their screen the same way: connections
                first, subscriptions second.

                ⚠️ **THIS ONE BRINGS ITS OWN `card-hive` WRAPPER, UNLIKE THE TWO BELOW, AND
                THAT IS NOT AN INCONSISTENCY TO TIDY UP.** It renders nothing at all when
                Microsoft sign-in has not been configured. With the heading out here, a
                brand-new installation would show an empty card titled "Microsoft calendar"
                with nothing inside it — which reads as a broken feature rather than an
                absent one. A component that can decide not to exist has to own its frame.
              */}
              <CalendarConnectionsCard />

              {/*
                Calendar feed links (ROADMAP §7). Same reasoning as the block above —
                its own component, not another 180 lines in here.
              */}
              <div className="card-hive p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Calendar feed</h3>
                    <p className="text-sm text-muted-foreground">
                      Your leave in Google Calendar, Outlook or Apple Calendar
                    </p>
                  </div>
                </div>
                <CalendarFeedCard />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
